import {
  createEffect,
  createSignal,
  For,
  JSXElement,
  mergeProps
} from 'solid-js';
import {
  getApiCredentialsFromResponse,
  handleAddRedirectUriError
} from '~/pages/ApiCredentials/utils.tsx';
import { appConfig } from '~/consts.ts';
import {
  ApiConfigData,
  ApiConfigUpdateRequest,
  ApiErrorInstance,
  updateClientCredentials
} from '~/api';
import { cloneObject } from '~/utils/commonUtils.ts';
import { useQueryClient } from '@tanstack/solid-query';
import { useToast } from '~/components/ui/Toast';
import { useIntl } from '@cookbook/solid-intl';
import { useConfirm } from '~/components/ui/Modal';
import AddRedirectUriModal from '~/pages/FullStackAuthentication/Redirects/AddRedirectUriModal.tsx';
import RedirectUriContainer from '~/pages/FullStackAuthentication/Redirects/RedirectUriContainer.tsx';
import {
  generateQueryKeysForRefetching,
  generateURLBlockFromFlowType,
  getDeleteConfirmationParamsForRedirectUri,
  getRedirectUriHeader,
  redirectUriApiUpdateRequestKey,
  RedirectUriFlowTypes,
  RedirectUriType
} from '~/pages/FullStackAuthentication/Redirects/utils.tsx';
import { getLocalizedString } from '~/i18n/utils.ts';
import { getRedirectsTranslationKey } from '~/pages/FullStackAuthentication/utils.ts';

type RedirectUriListProps = {
  clientCredentials?: ApiConfigData[];
  flowType?: RedirectUriFlowTypes;
};

type RedirectUriTypeWithoutInitiate = Exclude<
  RedirectUriType,
  RedirectUriType.INITIATE_LOGIN_URI
>;

export default function RedirectUriList(
  props: RedirectUriListProps
): JSXElement {
  const merged = mergeProps({}, props);

  const queryClient = useQueryClient();

  const apiCredential = () =>
    getApiCredentialsFromResponse(merged.clientCredentials);

  const toaster = useToast();
  const intl = useIntl();
  const confirm = useConfirm();

  const [showModal, setShowModal] = createSignal(false);
  const [uriToEdit, setUriToEdit] = createSignal<string>('');
  const [redirectUriType, setRedirectUriType] = createSignal<RedirectUriType>();

  createEffect(() => {
    if (!!uriToEdit()) {
      setShowModal(true);
    }
  });

  const redirectUriConfig = () => ({
    [RedirectUriType.INITIATE_LOGIN_URI]: apiCredential().initiateLoginUri,
    [RedirectUriType.POST_LOGIN_URI]: apiCredential().postLoginUris,
    [RedirectUriType.POST_LOGOUT_URI]: apiCredential().postLogoutRedirectUris,
    [RedirectUriType.BACK_CHANNEL_LOGOUT_URI]:
      apiCredential().backChannelLogoutUris
  });

  const refetchClientCredentials = async () => {
    await queryClient.refetchQueries({
      queryKey: generateQueryKeysForRefetching(merged?.flowType),
      type: 'all'
    });
  };

  const handleRedirectUriUpdate = async (
    redirectUri: string,
    uriToReplace: string
  ) => {
    try {
      const dataPayload: ApiConfigUpdateRequest = buildUpdateRequest(
        redirectUri,
        uriToReplace
      );
      const updatedData = await updateClientCredentials(
        apiCredential().id,
        dataPayload
      );

      await refetchClientCredentials();
      setShowModal(false);
      setUriToEdit('');
      return updatedData;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const buildUpdateRequest = (
    redirectUri: string,
    uriToReplace: string
  ): ApiConfigUpdateRequest => {
    return redirectUriType() === RedirectUriType.INITIATE_LOGIN_URI
      ? { initiateLoginUri: redirectUri }
      : buildRedirectUriDataPayload(
          redirectUriConfig()[
            redirectUriType() as RedirectUriTypeWithoutInitiate
          ],
          redirectUri,
          uriToReplace,
          redirectUriApiUpdateRequestKey[redirectUriType() as RedirectUriType]
        );
  };

  const buildRedirectUriDataPayload = (
    uris: string[] = [],
    redirectUri: string,
    uriToReplace: string,
    key: string
  ) => {
    const clonedUris = cloneObject(uris) || [];

    if (!uriToReplace) {
      clonedUris.push(redirectUri);
    } else {
      const updatedUris = clonedUris.map((uri) =>
        uri === uriToReplace ? redirectUri : uri
      );
      return { [key]: updatedUris };
    }

    return { [key]: clonedUris };
  };

  const handleDeleteRedirectUri = (
    uriType: RedirectUriType,
    redirectUri: string
  ) => {
    const params = getDeleteConfirmationParamsForRedirectUri(uriType, intl);
    confirm.show({
      title: params.title,
      description: params.description,
      primaryActionLabel: params.ctaLabel,
      onConfirm: async () => {
        try {
          confirm.setIsLoading(true);
          const data: ApiConfigUpdateRequest = {
            [redirectUriApiUpdateRequestKey[uriType]]:
              uriType !== RedirectUriType.INITIATE_LOGIN_URI
                ? redirectUriConfig()[uriType].filter(
                    (uri) => uri !== redirectUri
                  )
                : ''
          };

          await updateClientCredentials(apiCredential().id, data);
          await refetchClientCredentials();
          confirm.close();
          toaster.toast().success({
            description: getLocalizedString(
              getRedirectsTranslationKey('delete_redirect_uri_success'),
              {
                intl,
                title: getRedirectUriHeader(uriType, intl).toLowerCase()
              }
            )
          });
        } catch (e) {
          confirm.setIsLoading(false);
          confirm.close();
          console.error(e);
          toaster.toast().error({
            description: handleAddRedirectUriError({
              error: e as ApiErrorInstance,
              intl,
              params: { genericErrorKey: 'delete_redirect_uri_failure' }
            })
          });
        }
      }
    });
  };

  return (
    <>
      <AddRedirectUriModal
        redirectUris={redirectUriConfig()[redirectUriType() as RedirectUriType]}
        open={showModal()}
        redirectUriType={redirectUriType()}
        onClose={() => {
          setShowModal(false);
          setUriToEdit('');
        }}
        uriToEdit={uriToEdit()}
        onRedirectUriAdded={handleRedirectUriUpdate}
      />

      <For
        each={
          generateURLBlockFromFlowType(merged?.flowType, apiCredential())
          // merged.flowType === RedirectUriFlowTypes.SSO_QUICK_START
          //   ? [RedirectUriType.POST_LOGIN_URI]
          //   : Object.values(RedirectUriType)
        }
      >
        {(uriType, index) => (
          <RedirectUriContainer
            type={uriType}
            flowType={merged.flowType}
            onCtaClick={() => {
              setShowModal(true);
              setRedirectUriType(uriType);
            }}
            isCtaDisabled={
              uriType === RedirectUriType.INITIATE_LOGIN_URI
                ? !!redirectUriConfig()[uriType]
                : redirectUriConfig()[uriType]?.length >=
                  appConfig.maxSupportedRedirectUtis
            }
            redirectUris={
              uriType === RedirectUriType.INITIATE_LOGIN_URI
                ? redirectUriConfig()[uriType]
                  ? [redirectUriConfig()[uriType]]
                  : []
                : redirectUriConfig()[uriType]
            }
            onEditClick={(redirectUri) => {
              setUriToEdit(redirectUri);
              setRedirectUriType(uriType);
            }}
            onDeleteClick={(redirectUri) =>
              handleDeleteRedirectUri(uriType, redirectUri)
            }
            {...(index() === 0 && {
              containerClass: 'mt-0'
            })}
          />
        )}
      </For>
    </>
  );
}
